import { O, pipe } from '@mobily/ts-belt';
import { Alert, Box, Button, CircularProgress, Grid, Stack, TextField, Typography } from '@mui/material';
import { useTranslation } from 'common/hooks/useTranslation';
import { Lambda } from 'common/types';
import { FC, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useCreateDropAntenna } from 'app/areas/hooks/useCreate';
import { useAreaPhotoUpload } from 'app/areas/hooks/useAreaPhotoUpload';
import { DropAntennaSchema, dropAntennaSchema } from 'app/areas/types/DropAntennaSchema';
import { PhotosField } from 'app/areas/components/PhotosField/PhotosField';
import { useErrorMessage } from 'app/ui/validationError/useErrorMessage';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { DropAntennaDetails } from 'app/areas/types/ObjectDetails';
import { EnlargedPhotoDialog } from '../EnlargedPhotoDialog';

import { OptionalSelect } from '../../ui/OptionalSelect';
import { useGetPropertyRadioConnections } from '../../hooks/useGetPropertyRadioConnections';
import { getRadioObject } from '../../getRadioObject';
import { getAreasRadio } from '../../getAreas';
import { DropAntenna } from '../ObjectDetails/DropAntenna';

export const CreateDropAntenna: FC<{ onSave: Lambda<void, void> }> = ({ onSave }) => {
  const { status, data } = useGetPropertyRadioConnections();
  const [objectId, setObjectId] = useState<O.Option<string>>(O.None);
  const { t } = useTranslation();

  const [pickedImage, setPickedImage] = useState<O.Option<string>>(O.None);

  // Get pre-generated entity ID and property ID for offline photo tracking
  const { entityId: preGeneratedId, entityType, propertyId } = useAreaPhotoUpload('dropAntenna');

  const { mutate: createDropAntenna, isLoading: isLoadingCreateDropAntenna } = useCreateDropAntenna({
    onSuccess: () => {
      onSave();
      toast.success(t('general.updatedSuccessfully'));
    },
    onError: () => {
      toast.error(t('error.generalMessage'));
    },
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<DropAntennaSchema>({
    defaultValues: {
      text: '',
      location: '',
      portCount: 1,
      unitType: '',
      serialNumber: '',
    },
    mode: 'onChange',
    resolver: zodResolver(dropAntennaSchema),
  });

  const [photosURLs, setPhotosURLs] = useState<DropAntennaDetails['photosURLs']>([]);

  const getErrorMessage = useErrorMessage(errors);

  if (status === 'loading') {
    return <CircularProgress />;
  }

  if (status === 'error') {
    return <Alert severity="error">{t('areas.properties.error')}</Alert>;
  }

  const { dropAntennas } = getAreasRadio(data);

  const selectedObjectDetails = pipe(
    objectId,
    O.flatMap((id) => getRadioObject(id)(data)),
  );

  const handleSave = (values: DropAntennaSchema) => {
    // Filter blob URLs and use pre-generated ID so photos are associated with this entity
    createDropAntenna({ ...values, id: preGeneratedId, photosURLs });
  };

  return (
    <Stack>
      {!objectId && (
        <>
          <Box my={2}>
            <EnlargedPhotoDialog
              photoUrl={pickedImage}
              onClose={() => {
                setPickedImage(O.None);
              }}
            />
            <Stack component="form" onSubmit={handleSubmit(handleSave)}>
              <Typography sx={{ fontSize: 18, paddingBottom: 2 }}>{t('areas.dropAntenna.label')}</Typography>
              <Stack gap={2} width="100%">
                <TextField label={t('areas.fields.name')} {...register('text')} helperText={getErrorMessage('text')} />
                <TextField
                  label={t('areas.fields.portCount')}
                  {...register('portCount', {
                    valueAsNumber: true,
                  })}
                  type="number"
                  helperText={getErrorMessage('portCount')}
                  disabled
                />
                <PhotosField
                  value={photosURLs}
                  onChange={setPhotosURLs}
                  onItemClick={(photoUrl) => {
                    setPickedImage(O.Some(photoUrl));
                  }}
                  entityType={entityType}
                  entityId={preGeneratedId}
                  propertyId={propertyId}
                />
                <TextField
                  label={t('areas.fields.location')}
                  {...register('location')}
                  helperText={getErrorMessage('location')}
                />
                <TextField
                  label={t('areas.fields.unitType')}
                  {...register('unitType')}
                  helperText={getErrorMessage('unitType')}
                />
                <TextField
                  label={t('areas.fields.serialNumber')}
                  {...register('serialNumber')}
                  helperText={getErrorMessage('serialNumber')}
                />
              </Stack>
              <Grid container justifyContent="space-between" mt={2}>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={!isValid || isLoadingCreateDropAntenna}
                >
                  {isLoadingCreateDropAntenna ? <CircularProgress size={16} /> : t('general.create')}
                </Button>
              </Grid>
            </Stack>
          </Box>
          <Box mt={2}>
            <Typography sx={{ fontSize: 18, paddingBottom: 2 }}>{t('areas.object.edit')}</Typography>
          </Box>
        </>
      )}
      {dropAntennas.length === 0 ? (
        <Alert severity="error" sx={{ width: 'unset' }}>
          {t('areas.object.noAvailable')}
        </Alert>
      ) : (
        <>
          <OptionalSelect
            options={dropAntennas.map(({ id, text }) => ({ id, label: O.getWithDefault(text, id) }))}
            value={objectId}
            onChange={setObjectId}
          />
          <Box mt={2}>
            {O.match(
              selectedObjectDetails,
              (details) => {
                if (details.type !== 'DropAntenna') {
                  return null;
                }
                return <DropAntenna key={details.id} details={details} onSave={onSave} />;
              },
              () => (
                <Alert severity="info" sx={{ width: 'unset' }}>
                  {t('areas.object.noSelected')}
                </Alert>
              ),
            )}
          </Box>
        </>
      )}
    </Stack>
  );
};
